import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { Header, Text } from "@redotech/arbiter/text";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import { sendMagicLink } from "@redotech/redo-referrals-api/schema/auth/send-magic-link";
import Logo from "@redotech/redo-web/logo.svg";
import { ClientError } from "@redotech/rpc/errors";
import {
  type FormEventHandler,
  type ReactNode,
  memo,
  useCallback,
  useMemo,
  useState,
} from "react";

import { useRedoReferralsRpcClient } from "../rpc/client-context";
import { LoginHeroPanel } from "./login-hero-panel";
import * as loginCss from "./login.module.css";

const TOO_MANY_REQUESTS_MESSAGE =
  "Too many requests. Please wait a moment and try again.";
const GENERIC_FAILURE_MESSAGE =
  "Couldn't send the login link. Please try again.";

function AuthIntro({
  title,
  subtitle,
}: {
  title: string;
  subtitle: ReactNode;
}) {
  return (
    <Flex align="center" dir="column" gap="xs">
      <Header
        as="h1"
        fontSize="xxs"
        fontWeight="semibold"
        m="none"
        textAlign="center"
      >
        {title}
      </Header>
      <Text
        fontSize="sm"
        fontWeight="thin"
        m="none"
        textAlign="center"
        textColor="tertiary"
      >
        {subtitle}
      </Text>
    </Flex>
  );
}

export const LoginPage = memo(function LoginPage() {
  const rpcClient = useRedoReferralsRpcClient();
  const [email, setEmail] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  // Same Zod schema as the server, so the form fails before the request.
  const validation = useMemo(
    () => sendMagicLink.input.safeParse({ email }),
    [email],
  );
  const validationError = !validation.success
    ? (validation.error.issues[0]?.message ?? "Enter a valid email")
    : undefined;

  const handleSubmit: FormEventHandler<HTMLDivElement> = useCallback(
    async (event) => {
      event.preventDefault();
      setEmailTouched(true);
      if (!validation.success) {
        return;
      }
      setError(undefined);
      setPending(true);
      try {
        await rpcClient.sendMagicLink(validation.data);
        setSubmittedEmail(validation.data.email);
      } catch (err) {
        if (err instanceof ClientError && err.statusCode === 429) {
          setError(err.message || TOO_MANY_REQUESTS_MESSAGE);
        } else {
          setError(GENERIC_FAILURE_MESSAGE);
        }
      } finally {
        setPending(false);
      }
    },
    [rpcClient, validation],
  );

  const handleUseDifferentEmail = useCallback(() => {
    setSubmittedEmail(undefined);
    setError(undefined);
  }, []);

  const showFieldError = emailTouched && validationError !== undefined;

  return (
    <Flex className={loginCss.container} gap="none" w="full">
      <Flex
        bgColor="primary"
        className={loginCss.leftPanel}
        dir="column"
        grow={1}
      >
        <Flex pt="3xl" px="3xl">
          <a
            className={loginCss.logoLockup}
            href="https://www.redo.com"
            rel="noreferrer"
            target="_blank"
          >
            <Logo className={loginCss.logo} />
            <Text
              as="span"
              fontSize="lg"
              fontWeight="regular"
              m="none"
              textColor="primary"
            >
              Referrals
            </Text>
          </a>
        </Flex>
        <Flex
          align="center"
          className={loginCss.formWrapper}
          grow={1}
          justify="center"
          pb="6xl"
        >
          <Flex
            as="section"
            className={loginCss.formArea}
            dir="column"
            gap="3xl"
            w="full"
          >
            {submittedEmail === undefined ? (
              <Flex as="form" dir="column" gap="3xl" onSubmit={handleSubmit}>
                <Flex dir="column" gap="3xl">
                  <AuthIntro
                    subtitle="Enter your email to receive a magic link."
                    title="Sign in to Redo Referrals"
                  />
                  <RedoTextInput
                    autoFocus
                    errorText={showFieldError ? validationError : undefined}
                    fullWidth
                    inputTypeHint="email"
                    label="Email address"
                    onBlur={() => setEmailTouched(true)}
                    placeholder="Enter email address"
                    setValue={setEmail}
                    value={email}
                  />
                </Flex>
                <Flex dir="column" gap="lg">
                  <RedoButton
                    buttonClassName={loginCss.primaryActionButton}
                    className={loginCss.fullWidthButton}
                    disabled={!validation.success}
                    hierarchy="primary"
                    pending={pending}
                    size="sm"
                    text="Send magic link"
                    type="submit"
                  />
                  {error && (
                    <Text fontSize="xs" m="none" textColor="error">
                      {error}
                    </Text>
                  )}
                </Flex>
              </Flex>
            ) : (
              <>
                <RedoButton
                  hierarchy="link"
                  IconLeading={ArrowLeftIcon}
                  onClick={handleUseDifferentEmail}
                  size="sm"
                  text="Use a different email"
                />
                <Flex dir="column" gap="3xl">
                  <AuthIntro
                    subtitle={
                      <>
                        We sent a login link to{" "}
                        <Text
                          as="span"
                          fontSize="sm"
                          fontWeight="semibold"
                          m="none"
                        >
                          {submittedEmail}
                        </Text>
                        .
                      </>
                    }
                    title="Check your email"
                  />
                  <Text fontSize="xs" m="none" textColor="secondary">
                    The link expires in 1 hour. If you don't see it, check your
                    spam folder.
                  </Text>
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
      <LoginHeroPanel />
    </Flex>
  );
});
